import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface ModerationActionsProps {
  targetType: 'thread' | 'comment'
  targetId: string
  contentAuthorId: string | null
  onActionComplete: () => void
}

export default function ModerationActions({
  targetType,
  targetId,
  contentAuthorId,
  onActionComplete,
}: ModerationActionsProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [actionNote, setActionNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const logModerationAction = useMutation({
    mutationFn: async (data: {
      action: 'hide' | 'warn' | 'ban' | 'restore'
      note: string | null
    }) => {
      if (!user) throw new Error('Not authenticated')

      // Log the action
      const { error: logError } = await supabase
        .from('moderation_actions')
        .insert({
          target_type: targetType,
          target_id: targetId,
          moderator_id: user.id,
          action: data.action,
          note: data.note || null,
        })

      if (logError) throw logError
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moderation-actions'] })
    },
  })

  const hideContent = useMutation({
    mutationFn: async () => {
      const table = targetType === 'thread' ? 'threads' : 'comments'
      const { error } = await supabase
        .from(table)
        .update({ is_hidden: true })
        .eq('id', targetId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-threads'] })
      queryClient.invalidateQueries({ queryKey: ['thread-comments'] })
      queryClient.invalidateQueries({ queryKey: ['threads-from-my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['latest-threads'] })
    },
  })

  const restoreContent = useMutation({
    mutationFn: async () => {
      const table = targetType === 'thread' ? 'threads' : 'comments'
      const { error } = await supabase
        .from(table)
        .update({ is_hidden: false })
        .eq('id', targetId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-threads'] })
      queryClient.invalidateQueries({ queryKey: ['thread-comments'] })
      queryClient.invalidateQueries({ queryKey: ['threads-from-my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['latest-threads'] })
    },
  })

  const banUser = useMutation({
    mutationFn: async () => {
      if (!contentAuthorId) throw new Error('No author ID available')

      const { error } = await supabase
        .from('profiles')
        .update({ is_banned: true })
        .eq('id', contentAuthorId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })

  const unbanUser = useMutation({
    mutationFn: async () => {
      if (!contentAuthorId) throw new Error('No author ID available')

      const { error } = await supabase
        .from('profiles')
        .update({ is_banned: false })
        .eq('id', contentAuthorId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })

  const handleAction = async (action: 'hide' | 'warn' | 'ban' | 'restore') => {
    if (!confirm(`Are you sure you want to ${action} this ${targetType}?`)) {
      return
    }

    setIsSubmitting(true)
    try {
      // Perform the action
      if (action === 'hide') {
        await hideContent.mutateAsync()
      } else if (action === 'restore') {
        await restoreContent.mutateAsync()
      } else if (action === 'ban') {
        await banUser.mutateAsync()
      } else if (action === 'warn') {
        // Warning is just logged, no content/user change
      }

      // Log the moderation action
      await logModerationAction.mutateAsync({
        action,
        note: actionNote.trim() || null,
      })

      setActionNote('')
      onActionComplete()
    } catch (err: any) {
      alert(err.message || `Failed to ${action} ${targetType}`)
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      <h3
        className="text-lg font-bold"
        style={{ color: 'var(--text-primary)' }}
      >
        Moderation Actions
      </h3>

      {/* Action Note */}
      <div>
        <label
          htmlFor="action-note"
          className="block mb-2 font-semibold"
          style={{ color: 'var(--text-primary)' }}
        >
          Action Note (Optional)
        </label>
        <textarea
          id="action-note"
          value={actionNote}
          onChange={(e) => setActionNote(e.target.value)}
          placeholder="Add a note about this moderation action..."
          rows={3}
          className="w-full px-4 py-2 border-2 rounded"
          style={{
            borderColor: 'var(--bg-tertiary)',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
          }}
          disabled={isSubmitting}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => handleAction('hide')}
          disabled={isSubmitting}
          className="px-4 py-2 font-semibold rounded disabled:opacity-50"
          style={{
            backgroundColor: 'var(--color-error)',
            color: 'var(--text-inverse)',
          }}
        >
          Hide Content
        </button>
        <button
          onClick={() => handleAction('warn')}
          disabled={isSubmitting}
          className="px-4 py-2 font-semibold rounded disabled:opacity-50"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
        >
          Warn User
        </button>
        {contentAuthorId && (
          <button
            onClick={() => handleAction('ban')}
            disabled={isSubmitting}
            className="px-4 py-2 font-semibold rounded disabled:opacity-50"
            style={{
              backgroundColor: 'var(--color-error)',
              color: 'var(--text-inverse)',
            }}
          >
            Ban User
          </button>
        )}
        <button
          onClick={() => handleAction('restore')}
          disabled={isSubmitting}
          className="px-4 py-2 font-semibold rounded disabled:opacity-50"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
        >
          Restore Content
        </button>
      </div>
    </div>
  )
}

